import { useRecoilValue } from 'recoil';
import { useToast } from '@chakra-ui/react';
import { useCallback } from 'react';
import {
  isReadOnlySelector,
  isLicenseValidSelector,
  licenseStatusSelector,
  isLicenseExpiringSoonSelector,
  daysUntilExpirySelector,
} from '../state/licenseAtom';

/**
 * Hook to get license protection status and helpers
 * Use this to disable create/edit/delete functionality when license is invalid
 */
export function useLicenseProtection() {
  const isReadOnly = useRecoilValue(isReadOnlySelector);
  const isLicenseValid = useRecoilValue(isLicenseValidSelector);
  const licenseStatus = useRecoilValue(licenseStatusSelector);
  const isExpiringSoon = useRecoilValue(isLicenseExpiringSoonSelector);
  const daysUntilExpiry = useRecoilValue(daysUntilExpirySelector);
  const toast = useToast();

  // Show warning toast when trying to perform action in read-only mode
  const showReadOnlyWarning = useCallback(() => {
    toast({
      title: 'License Required',
      description: 'A valid license is required to make changes. Please contact your administrator.',
      status: 'warning',
      duration: 5000,
      isClosable: true,
    });
  }, [toast]);

  // Wrapper that prevents action if in read-only mode
  const protectAction = useCallback(
    (action) => {
      if (isReadOnly) {
        showReadOnlyWarning();
        return;
      }
      action();
    },
    [isReadOnly, showReadOnlyWarning]
  );

  // Get message based on license status
  const getStatusMessage = useCallback(() => {
    switch (licenseStatus) {
      case 'none':
        return 'No license key configured';
      case 'expired':
        return 'Your license has expired';
      case 'invalid':
        return 'Your license key is invalid';
      case 'active':
        if (isExpiringSoon && daysUntilExpiry !== null) {
          return `License expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}`;
        }
        return 'License is active';
      default:
        return 'License status unknown';
    }
  }, [licenseStatus, isExpiringSoon, daysUntilExpiry]);

  return {
    isReadOnly,
    isLicenseValid,
    licenseStatus,
    isExpiringSoon,
    daysUntilExpiry,
    showReadOnlyWarning,
    protectAction,
    getStatusMessage,
  };
}

export default useLicenseProtection;
